import React, { useEffect, useRef, useState } from 'react';
import { nextShownChars, safeCutIndex, PACE_TICK_MS, REPLAY_CHARS_PER_MS } from '../../utils/streamPacing';
import { preloadFavicon } from '../../utils/faviconPreload';

interface Props {
  /** Full text received so far (grows as SSE chunks arrive). */
  text: string;
  /** Whether new deltas should be revealed at the paced streaming rate. */
  streaming?: boolean;
  /** Renders the visible slice (the existing markdown pipeline). */
  render: (visible: string) => React.ReactNode;
  /** Text that was held back (compact status) and appears now already long:
      reveal it from the start at REPLAY_CHARS_PER_MS — a simulated stream —
      instead of painting it at once. Calls onRevealed when caught up. */
  revealFromStart?: boolean;
  onRevealed?: () => void;
}

/**
 * Paces the reveal of a streaming text item: instead of painting each SSE
 * chunk at once, advances a cursor toward the end of the text on an rAF loop
 * (throttled to ~20fps), draining the buffered delta over ~300ms. Mounts at
 * the CURRENT length (no replay of already-shown text) and only paces the
 * new deltas. The stable wrapper stays mounted when streaming finishes so the
 * markdown subtree is reconciled in place instead of flashing on a remount.
 */
const PacedText: React.FC<Props> = ({ text, streaming = false, render, revealFromStart = false, onRevealed }) => {
  const targetRef = useRef(text.length);
  targetRef.current = text.length;
  // Start where the text already is: pacing applies only to what arrives next.
  // A held answer starts from zero and replays (simulated stream).
  const [shown, setShown] = useState(revealFromStart ? 0 : text.length);
  // The replay runs to the length the text had on mount; later deltas pace normally.
  const replayToRef = useRef(revealFromStart ? text.length : 0);
  const [replaying, setReplaying] = useState(revealFromStart);
  const onRevealedRef = useRef(onRevealed);
  onRevealedRef.current = onRevealed;

  useEffect(() => {
    if (!streaming && !replaying) return;
    let raf = 0;
    let last = performance.now();
    const tick = (now: number) => {
      const dt = now - last;
      if (dt >= PACE_TICK_MS) {
        last = now;
        setShown((s) => (s < replayToRef.current
          ? Math.min(replayToRef.current, s + Math.ceil(dt * REPLAY_CHARS_PER_MS))
          : nextShownChars(s, targetRef.current, dt)));
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [streaming, replaying]);

  useEffect(() => {
    if (replaying && shown >= targetRef.current) {
      setReplaying(false);
      if (onRevealedRef.current) onRevealedRef.current();
    }
  }, [replaying, shown]);

  const sliced = (!streaming && !replaying) || shown >= text.length ? text : text.slice(0, shown);
  // Never show a half-streamed link/citation: hold until the construct closes
  // so it renders once, straight as the final chip (no raw->link->chip flicker).
  const cut = safeCutIndex(sliced);
  const visible = cut < sliced.length ? sliced.slice(0, cut) : sliced;

  // The held tail often already contains a complete domain ("https://host/..")
  // before the path finishes streaming — pre-warm the favicon now so the chip
  // shows up with its icon on first paint.
  const heldDomain = cut < sliced.length
    ? (/https?:\/\/([^\s<>)/]+)\//.exec(sliced.slice(cut))?.[1] || '')
    : '';
  useEffect(() => {
    if (heldDomain) preloadFavicon(heldDomain);
  }, [heldDomain]);

  return (
    <div className="paced-stream" style={{ display: 'contents' }}>
      {render(visible)}
    </div>
  );
};

export default PacedText;
